/**
 * FeeAssumptionsList Component
 * 9.9d CONTRACT-03: Individual fee assumption items with drag-drop reordering
 *
 * Displays project-level fee assumptions as a numbered list with:
 * - Drag-drop reordering via dnd-kit
 * - Inline editing
 * - Add new / delete items
 * - Quick-add from library
 */
import { useState, useMemo, useEffect } from 'react';
import { GripVertical, Plus, Trash2, Check, X, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useProjectFeeAssumptions,
  useCreateProjectFeeAssumption,
  useUpdateProjectFeeAssumption,
  useDeleteProjectFeeAssumption,
  useReorderProjectFeeAssumptions,
} from '@/hooks/useProjectFeeAssumptions';
import { useLibraryAssumptions } from '@/hooks/useFeeAssumptions';
import type { ProjectFeeAssumption } from '@/lib/schemas';

// ============================================================================
// Sortable Item Component
// ============================================================================

interface SortableItemProps {
  item: ProjectFeeAssumption;
  index: number;
  isEditing: boolean;
  editValue: string;
  onStartEdit: () => void;
  onChangeEdit: (value: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
  disabled?: boolean;
}

function SortableItem({
  item,
  index,
  isEditing,
  editValue,
  onStartEdit,
  onChangeEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  disabled,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 py-2 px-1 rounded-md group',
        isDragging && 'bg-blue-50',
        !isEditing && 'hover:bg-gray-50'
      )}
      data-testid={`fee-assumption-item-${item.id}`}
    >
      {/* Drag handle */}
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing touch-none"
        {...attributes}
        {...listeners}
        tabIndex={-1}
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4 text-gray-400 hover:text-gray-600" />
      </button>

      {/* Number */}
      <span className="text-sm font-medium text-gray-500 w-6 flex-shrink-0">
        {index + 1}.
      </span>

      {/* Content - editable or display */}
      {isEditing ? (
        <div className="flex-1 flex items-center gap-2">
          <Input
            value={editValue}
            onChange={(e) => onChangeEdit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSaveEdit();
              } else if (e.key === 'Escape') {
                onCancelEdit();
              }
            }}
            autoFocus
            className="flex-1 h-8"
            data-testid="fee-assumption-edit-input"
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onSaveEdit}
            className="h-8 w-8 p-0"
            disabled={!editValue.trim()}
          >
            <Check className="h-4 w-4 text-emerald-600" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onCancelEdit}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4 text-gray-500" />
          </Button>
        </div>
      ) : (
        <>
          <span
            className="flex-1 text-sm text-gray-700 cursor-pointer"
            onClick={onStartEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onStartEdit();
              }
            }}
            role="button"
            tabIndex={0}
            title="Click to edit"
          >
            {item.assumption_text}
          </span>

          {/* Delete button - visible on hover */}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onDelete}
            disabled={disabled}
            className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Delete"
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

interface FeeAssumptionsListProps {
  projectId: string;
  className?: string;
}

export function FeeAssumptionsList({ projectId, className }: FeeAssumptionsListProps) {
  // State
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [libraryOpen, setLibraryOpen] = useState(false);

  // Local state for optimistic reorder
  const [localItems, setLocalItems] = useState<ProjectFeeAssumption[]>([]);

  // Queries
  const { data, isLoading } = useProjectFeeAssumptions(projectId);
  const { data: libraryData } = useLibraryAssumptions();
  const library = libraryData?.fee_assumptions || [];

  // Mutations
  const createMutation = useCreateProjectFeeAssumption();
  const updateMutation = useUpdateProjectFeeAssumption();
  const deleteMutation = useDeleteProjectFeeAssumption();
  const reorderMutation = useReorderProjectFeeAssumptions();

  // Sync local items with server data
  const items = data?.fee_assumptions || [];
  useEffect(() => {
    if (data?.fee_assumptions) {
      setLocalItems(data.fee_assumptions);
    }
  }, [data?.fee_assumptions]);

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get item IDs for SortableContext
  const itemIds = useMemo(() => localItems.map((item) => item.id), [localItems]);

  // Handlers
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localItems.findIndex((item) => item.id === active.id);
      const newIndex = localItems.findIndex((item) => item.id === over.id);

      // Optimistic UI update
      const newItems = arrayMove(localItems, oldIndex, newIndex);
      setLocalItems(newItems);

      // Persist new order
      const reorderPayload = newItems.map((item, index) => ({
        id: item.id,
        display_order: index,
      }));
      reorderMutation.mutate({ projectId, items: reorderPayload });
    }
  };

  const handleStartEdit = (item: ProjectFeeAssumption) => {
    setEditingId(item.id);
    setEditValue(item.assumption_text);
  };

  const handleSaveEdit = () => {
    if (!editingId || !editValue.trim()) return;

    updateMutation.mutate(
      {
        projectId,
        assumptionId: editingId,
        data: { assumption_text: editValue.trim() },
      },
      {
        onSuccess: () => {
          setEditingId(null);
          setEditValue('');
        },
      }
    );
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleDelete = (assumptionId: string) => {
    deleteMutation.mutate({ projectId, assumptionId });
  };

  const handleAdd = () => {
    if (!newValue.trim()) return;

    createMutation.mutate(
      {
        projectId,
        data: { assumption_text: newValue.trim() },
      },
      {
        onSuccess: (response) => {
          // Optimistic: add new item to local state immediately so the list
          // updates without waiting for React Query to refetch from the server.
          const newItem = response?.fee_assumption;
          if (newItem) {
            setLocalItems(prev => [...prev, newItem]);
          }
          setNewValue('');
          setIsAdding(false);
        },
      }
    );
  };

  const handleAddFromLibrary = (assumptionText: string) => {
    createMutation.mutate(
      {
        projectId,
        data: { assumption_text: assumptionText },
      },
      {
        onSuccess: (response) => {
          // Optimistic: add library item to local state immediately.
          const newItem = response?.fee_assumption;
          if (newItem) {
            setLocalItems(prev => [...prev, newItem]);
          }
        },
      }
    );
    // Keep popover open for multiple selections
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('space-y-2', className)}>
        <div className="h-8 bg-gray-100 rounded animate-pulse" />
        <div className="h-8 bg-gray-100 rounded animate-pulse" />
        <div className="h-8 bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)} data-testid="fee-assumptions-list">
      {/* List items with drag-drop */}
      {localItems.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            <div className="space-y-1">
              {localItems.map((item, index) => (
                <SortableItem
                  key={item.id}
                  item={item}
                  index={index}
                  isEditing={editingId === item.id}
                  editValue={editValue}
                  onStartEdit={() => handleStartEdit(item)}
                  onChangeEdit={setEditValue}
                  onSaveEdit={handleSaveEdit}
                  onCancelEdit={handleCancelEdit}
                  onDelete={() => handleDelete(item.id)}
                  disabled={deleteMutation.isPending}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <p className="text-sm text-muted-foreground italic py-2">
          No fee assumptions yet. Add one below.
        </p>
      )}

      {/* Add new item */}
      {isAdding ? (
        <div className="flex items-center gap-2 py-2 px-1">
          <div className="w-6" /> {/* Spacer for alignment */}
          <span className="text-sm font-medium text-gray-500 w-6 flex-shrink-0">
            {localItems.length + 1}.
          </span>
          <Input
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleAdd();
              } else if (e.key === 'Escape') {
                setIsAdding(false);
                setNewValue('');
              }
            }}
            placeholder="Enter new assumption..."
            autoFocus
            className="flex-1 h-8"
            data-testid="fee-assumption-new-input"
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={handleAdd}
            disabled={!newValue.trim() || createMutation.isPending}
            className="h-8 w-8 p-0"
          >
            <Check className="h-4 w-4 text-emerald-600" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setIsAdding(false);
              setNewValue('');
            }}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4 text-gray-500" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2 pt-2">
          {/* Add button */}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setIsAdding(true)}
            className="h-8"
            data-testid="add-fee-assumption-button"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Assumption
          </Button>

          {/* Quick-add from library */}
          {library.length > 0 && (
            <Popover
              open={libraryOpen}
              onOpenChange={(open) => {
                // Keep popover open after selections - user closes via click outside or Escape
                // Only allow closing, not re-opening via this callback
                if (!open) {
                  setLibraryOpen(false);
                }
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8"
                  data-testid="add-from-library-button"
                  onClick={() => setLibraryOpen(!libraryOpen)}
                >
                  <ChevronsUpDown className="h-4 w-4 mr-1" />
                  From Library
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="start">
                <Command
                  onKeyDown={(e) => {
                    // Prevent Command from closing popover on Enter/selection
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                >
                  <CommandInput placeholder="Search library..." />
                  <CommandList>
                    <CommandEmpty>No assumptions found</CommandEmpty>
                    <CommandGroup>
                      {library.map((assumption) => {
                        const isAdded = localItems.some(
                          (item) => item.assumption_text === assumption.assumption_text
                        );
                        return (
                          <CommandItem
                            key={assumption.id}
                            value={assumption.assumption_text}
                            onSelect={(currentValue) => {
                              // Add to project without closing popover
                              handleAddFromLibrary(assumption.assumption_text);
                              // Don't let Command component trigger popover close
                            }}
                            disabled={isAdded}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                isAdded ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            <span className={cn(isAdded && 'text-muted-foreground')}>
                              {assumption.assumption_text}
                            </span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}
    </div>
  );
}
